import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Twitter, Youtube, Facebook } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      // Navigate to home with hash, HomePage will handle scroll
      navigate(`/#${id}`);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const goHome = () => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const navLinks = [
    { label: 'Services', href: 'services' },
    { label: 'Elite Program', href: 'elite-program' },
    { label: 'Schedule', href: 'schedule' },
    { label: 'Videos', href: 'videos' },
    { label: 'Contact', href: 'contact' }
  ];

  const socialLinks = [
    { icon: Instagram, url: 'https://www.instagram.com/coachmattpugh/', label: 'Instagram' },
    { icon: Twitter, url: 'https://x.com/CoachMattPugh', label: 'Twitter' },
    { icon: Youtube, url: 'https://www.youtube.com/@mattpughpuresweatbasketbal6908', label: 'YouTube' },
    { icon: Facebook, url: 'https://www.facebook.com/mattpughpuresweat/', label: 'Facebook' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <button
            onClick={goHome}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            data-testid="logo-button"
          >
            <img
              src="/hoopiq-logo-transparent.png"
              alt="HoopIQ"
              className="h-12 md:h-14 w-auto object-contain"
              style={{ filter: 'invert(1) hue-rotate(180deg)' }}
            />
          </button>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-sm font-semibold uppercase tracking-wide text-white hover:text-[#C9A84C]"
                data-testid={`nav-link-${link.href}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-[#C9A84C] transition-colors"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                >
                  <Icon size={18} />
                </a>
              );
            })}
            <button
              onClick={() => scrollToSection('contact')}
              className="ml-4 bg-[#C9A84C] text-[#061324] px-6 py-2 rounded-sm font-bold uppercase tracking-wide text-sm hover:bg-[#d8b655] transition-colors gold-glow"
              data-testid="cta-train-button"
            >
              Train With Us
            </button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="lg:hidden glass-effect border-t border-white/10" data-testid="mobile-menu">
          <nav className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="block w-full text-left text-base font-semibold uppercase tracking-wide text-white hover:text-[#C9A84C] py-2"
                data-testid={`mobile-nav-${link.href}`}
              >
                {link.label}
              </button>
            ))}
            <div className="flex items-center gap-4 pt-4 border-t border-white/10">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-[#C9A84C] transition-colors"
                    aria-label={social.label}
                    data-testid={`mobile-social-${social.label.toLowerCase()}`}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};